import { Link } from "react-router-dom";
import { Twitter, Youtube, Twitch, MessageCircle, Mail, Instagram } from "lucide-react";
import { LOGO } from "./assets";
import { useLang } from "./LanguageContext";

const CONTACT_EMAIL = "inoelitesofficial@gmail.com";

const SOCIALS = [
  { icon: Instagram, label: "instagram", href: "https://www.instagram.com/inoelites/" },
  { icon: Twitter, label: "twitter", href: "#" },
  { icon: Youtube, label: "youtube", href: "#" },
  { icon: Twitch, label: "twitch", href: "#" },
  { icon: MessageCircle, label: "discord", href: "#" },
];

const COLS = [
  { titleKey: "footer.colGame", links: [
    { key: "systems", href: "/#features" },
    { key: "genres", href: "/#genres" },
    { key: "world", href: "/#world" },
    { key: "arenas", href: "/#media" },
  ]},
  { titleKey: "footer.colStudio", links: [
    { key: "about", href: "#" },
    { key: "careers", href: "#" },
    { key: "presskit", href: "#" },
    { key: "news", href: "#" },
  ]},
  { titleKey: "footer.colSupport", links: [
    { key: "faq", href: "#" },
    { key: "contact", href: `mailto:${CONTACT_EMAIL}` },
    { key: "privacy", to: "/privacy" },
    { key: "terms", to: "/terms" },
  ]},
];

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer data-testid="footer" className="relative bg-[#060A14] border-t border-[#00F2FE]/10 pt-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 pb-16">
          <div className="col-span-2 md:col-span-1">
            <img src={LOGO} alt="Ino Elites" className="h-10 w-auto mb-5 drop-shadow-[0_0_12px_rgba(0,242,254,0.45)]" />
            <p className="font-mono-x text-xs text-slate-400 leading-relaxed max-w-xs">{t("footer.tagline")}</p>

            <p className="font-mono-x text-[10px] font-bold tracking-[0.3em] text-[#F9D423] mt-6 mb-2">{t("footer.contactLabel")}</p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="footer-contact-email"
              className="flex items-center gap-2 text-sm text-slate-300 hover:text-[#00F2FE] transition-colors break-all"
            >
              <Mail size={15} className="shrink-0" /> {CONTACT_EMAIL}
            </a>

            <div className="flex gap-4 mt-6">
              {SOCIALS.map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    target={s.href.startsWith("http") ? "_blank" : undefined}
                    rel={s.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    data-testid={`footer-social-${s.label}`}
                    aria-label={s.label}
                    className="w-10 h-10 rounded-sm border border-white/15 flex items-center justify-center text-slate-400 hover:text-[#00F2FE] hover:border-[#00F2FE] transition-colors"
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          {COLS.map((c) => (
            <div key={c.titleKey}>
              <p className="font-mono-x text-xs font-bold tracking-[0.3em] text-[#F9D423] mb-5">{t(c.titleKey)}</p>
              <ul className="space-y-3">
                {c.links.map((l) => (
                  <li key={l.key}>
                    {l.to ? (
                      <Link
                        to={l.to}
                        data-testid={`footer-link-${l.key}`}
                        className="text-sm text-slate-400 hover:text-[#00F2FE] transition-colors"
                      >
                        {t(`footer.links.${l.key}`)}
                      </Link>
                    ) : (
                      <a
                        href={l.href}
                        data-testid={`footer-link-${l.key}`}
                        className="text-sm text-slate-400 hover:text-[#00F2FE] transition-colors"
                      >
                        {t(`footer.links.${l.key}`)}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="relative w-full overflow-hidden border-t border-white/10">
        <h2 className="font-display font-black uppercase text-center leading-none text-[20vw] text-outline select-none py-2">INO ELITES</h2>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex flex-col sm:flex-row justify-between items-center gap-3 border-t border-white/10">
        <p className="font-mono-x text-xs text-slate-600 tracking-[0.2em]">{t("footer.copyright")}</p>
        <p className="font-mono-x text-xs text-slate-600 tracking-[0.2em]">{t("footer.slogan")}</p>
      </div>
    </footer>
  );
};

export default Footer;
